/**
 * Echo Hunt -- Settings tab: the suspicion meter (P11, §8.4).
 *
 * One switch and three numbers. The switch is the one the user asked for: applying suspicion can be turned
 * OFF, and the panel is explicit about what that does and does not do --
 *
 *  - it does NOT stop the meter recording. `suspicion` keeps accumulating on every story.
 *  - it DOES stop the meter acting: no lucid interlude is written (so no tokens are spent), and the story
 *    is never ended by the threshold. It keeps going, and the number is a readout.
 *
 * Saying that in the panel matters more than usual here, because "off" has two plausible readings and the
 * destructive one -- discarding the history -- is not what happens. A user who assumed it did would switch
 * it back on and be surprised (by either outcome).
 *
 * See `Source/EchoHunt/Story.ts` for the rationale, and §8.4 of the plan.
 */
import React from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Row, Text} from "react-vcomponents";
import {InfoButton} from "web-vcore";
import {GetMyEchoProfile, GetSuspicionSettings, SetSuspicionSettings_Live} from "../../../EchoHunt/Profiles_Live.js";
import {DefaultSuspicionSettings, MIN_SUSPICION_THRESHOLD, ThresholdIsClamped} from "../../../EchoHunt/Story.js";
import {NumberField} from "./NumberField.js";

export const SuspicionPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();

	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile to configure the suspicion meter.</Text>;
	}

	// the NORMALIZED settings: the panel must show what the meter is actually using, not what is stored
	const settings = GetSuspicionSettings();
	const stored = profile.suspicion;
	const clamped = ThresholdIsClamped(stored);

	const change = (updates: Partial<typeof settings>)=>{
		SetSuspicionSettings_Live(updates).catch(ex=>console.error("Failed to save suspicion settings:", ex));
	};

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>Suspicion</Text>
				<InfoButton ml={5} text={[
					"§8.4: your correct echo finds raise the character's suspicion. Past the threshold they notice something",
					"is wrong, check, and become lucid -- narrated in an interlude which ends the story. The next story",
					"then follows a DIFFERENT character from your pool.",
					"",
					"That is the mechanism that ties your recognition to the story: noticing your own dreams IS what wakes",
					"the character up.",
					"",
					"Turning 'apply' off keeps the meter recording -- so it stays an honest record and can be switched back",
					"on later -- but stops it from ever interrupting or ending the story. Nothing about the weave, the judge",
					"loop or the round's events changes either way: the meter is computed from a finished round's score, and",
					"the only prompt that ever sees it is the closing interlude, which runs after the story has ended.",
				].join("\n")}/>
			</Row>

			<Row mt={8} center style={{gap: 10, flexWrap: "wrap"}}>
				<CheckBox text="Apply suspicion to the story" value={settings.applied}
					onChange={value=>change({applied: value})}/>
				<Text style={{fontSize: 12, opacity: .7}}>
					{settings.applied
						? "On: reaching the threshold ends the story with a lucid interlude."
						: "Off: the meter is recorded but never ends the story and never writes an interlude."}
				</Text>
			</Row>

			<Row mt={6} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{width: 130}}>Threshold:</Text>
				<NumberField value={settings.threshold} step={.05} min={MIN_SUSPICION_THRESHOLD} max={1}
					onChange={value=>change({threshold: value})}/>
				<Text style={{fontSize: 12, opacity: .65}}>
					{`0..1; reaching it ends the story. Floor is ${MIN_SUSPICION_THRESHOLD} -- 0 would end every story immediately.`}
				</Text>
			</Row>

			<Row mt={6} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{width: 130}}>Gain per find:</Text>
				<NumberField value={settings.gainPerHit} step={.02} min={0} max={1}
					onChange={value=>change({gainPerHit: value})}/>
				<Text style={{fontSize: 12, opacity: .65}}>
					{`what one correct echo adds. At ${settings.gainPerHit.toFixed(2)}, a story ends after about `
						+ `${settings.gainPerHit > 0 ? Math.ceil(settings.threshold / settings.gainPerHit) : "—"} found echoes.`}
				</Text>
			</Row>

			<Row mt={6} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{width: 130}}>Loss per miss:</Text>
				<NumberField value={settings.lossPerMiss} step={.01} min={0} max={1}
					onChange={value=>change({lossPerMiss: value})}/>
				<Text style={{fontSize: 12, opacity: .65}}>
					{"what one missed echo subtracts. Kept small: a miss is a failure of attention, not evidence that the character is awake."}
				</Text>
			</Row>

			{clamped &&
				<Text mt={6} style={{fontSize: 12, color: "orange"}}>
					{`The saved threshold (${stored?.threshold ?? "?"}) is below the ${MIN_SUSPICION_THRESHOLD} floor, so the meter is using ${MIN_SUSPICION_THRESHOLD}.`}
				</Text>}

			<Row mt={8} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontSize: 12, opacity: .6}}>
					{"These settings apply to every story. Changing them does not retroactively end a story that is already running."}
				</Text>
				<Button text="Restore defaults" onClick={()=>{
					change(DefaultSuspicionSettings());
				}}/>
			</Row>
		</>
	);
});
